import { type PlatformType } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { type CountryCode, type ProjectCode } from '@constants'
import { sumOfArray } from '@utils'
import { createContext, useMemo, type PropsWithChildren } from 'react'

interface FpB2bProductCatalogContextProps extends FpB2bProductCatalogProviderProps {
  cartTotalQuantity: number
  cartTotalPrice: number
  cartFrozenTotalPrice: number
  cartNormalTotalPrice: number
  currency?: string
}

export const FpB2bProductCatalogContext = createContext<FpB2bProductCatalogContextProps | null>(null)

export interface FpB2bProductCatalogProviderProps {
  countryCode: CountryCode
  projectCode: ProjectCode
  campaignId: number
  showButler?: boolean
  currency?: string
  campaignCode: string
  platform: PlatformType
}

export const FpB2bProductCatalogProvider = (props: PropsWithChildren<FpB2bProductCatalogProviderProps>) => {
  const { children, showButler, countryCode, projectCode, currency, campaignId, campaignCode, platform } = props
  const { items: cartItems } = useFpB2bCartService(campaignId, platform)

  const cartTotalQuantity = useMemo(() => sumOfArray(cartItems, (acc, { quantity = 0 }) => acc + quantity), [cartItems])

  const normalCartItems = useMemo(() => cartItems.filter(item => item.delivery_class === 'normal'), [cartItems])
  const frozenCartItems = useMemo(() => cartItems.filter(item => item.delivery_class === 'frozen'), [cartItems])

  const cartTotalPrice = useMemo(() => sumOfArray(cartItems, (acc, { sale_price = 0, quantity = 0 }) => acc + sale_price * quantity), [cartItems])
  const cartNormalTotalPrice = useMemo(() => sumOfArray(normalCartItems, (acc, { sale_price = 0, quantity = 0 }) => acc + sale_price * quantity), [normalCartItems])
  const cartFrozenTotalPrice = useMemo(() => sumOfArray(frozenCartItems, (acc, { sale_price = 0, quantity = 0 }) => acc + sale_price * quantity), [frozenCartItems])

  const contextValue = useMemo(
    () => ({
      cartTotalQuantity,
      cartTotalPrice,
      cartNormalTotalPrice,
      cartFrozenTotalPrice,
      showButler,
      countryCode,
      projectCode,
      currency,
      campaignId,
      campaignCode,
      platform,
    }),
    [cartTotalQuantity, cartTotalPrice, cartNormalTotalPrice, cartFrozenTotalPrice, showButler, countryCode, projectCode, currency, campaignId, campaignCode, platform],
  )

  return (
    <FpB2bProductCatalogContext.Provider value={contextValue}>
      {children}
    </FpB2bProductCatalogContext.Provider>
  )
}
